/**
 * Prayer times & Qibla direction — client-side, zero network calls.
 *
 * Prayer times are computed using the standard astronomical algorithm
 * (sun's hour angle for sunrise/sunset + solar noon) with the Muslim
 * World League angle set (Fajr 18°, Isha 17°). This is the method
 * recommended by most European and North-African authorities and is a
 * safe neutral default. Users in Umm al-Qura countries will see times
 * within a few minutes of their local almanac.
 *
 * Qibla is the forward great-circle bearing from the user's lat/lon
 * to the Kaaba (21.4225°N, 39.8262°E).
 *
 * No external API, no backend call, no PII. Everything stays local.
 */

const deg2rad = (d) => (d * Math.PI) / 180;
const rad2deg = (r) => (r * 180) / Math.PI;

// Kaaba coordinates (centre of the Mataaf), Mecca, Saudi Arabia.
export const KAABA = { lat: 21.4225, lon: 39.8262 };

/**
 * Great-circle bearing (initial) from `from` → `to`, in degrees true
 * (0 = north, 90 = east, 180 = south, 270 = west).
 */
export const qiblaBearing = ({ lat, lon }) => {
  const phi1 = deg2rad(lat);
  const phi2 = deg2rad(KAABA.lat);
  const dLon = deg2rad(KAABA.lon - lon);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  const theta = Math.atan2(y, x);
  return (rad2deg(theta) + 360) % 360;
};

// -- Astronomical helpers (Jean Meeus "Astronomical Algorithms") --------
const julianDay = (d) => {
  let y = d.getUTCFullYear();
  let m = d.getUTCMonth() + 1;
  const day = d.getUTCDate() + (d.getUTCHours() + d.getUTCMinutes() / 60) / 24;
  if (m <= 2) { y -= 1; m += 12; }
  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + B - 1524.5;
};

// Solar declination and equation of time for a given Julian day.
const sunPosition = (jd) => {
  const D = jd - 2451545.0;
  const g = (357.529 + 0.98560028 * D) % 360;
  const q = (280.459 + 0.98564736 * D) % 360;
  const L = (q + 1.915 * Math.sin(deg2rad(g)) + 0.020 * Math.sin(deg2rad(2 * g))) % 360;
  const e = 23.439 - 0.00000036 * D;
  const RA = rad2deg(Math.atan2(Math.cos(deg2rad(e)) * Math.sin(deg2rad(L)), Math.cos(deg2rad(L)))) / 15;
  const decl = rad2deg(Math.asin(Math.sin(deg2rad(e)) * Math.sin(deg2rad(L))));
  const EqT = (q / 15 - (RA + 24) % 24);
  return { decl, EqT };
};

// Hour angle for sun at a given altitude angle `alt` (deg below horizon).
const hourAngle = (alt, lat, decl) => {
  const cosT = (-Math.sin(deg2rad(alt)) - Math.sin(deg2rad(lat)) * Math.sin(deg2rad(decl)))
    / (Math.cos(deg2rad(lat)) * Math.cos(deg2rad(decl)));
  if (cosT < -1 || cosT > 1) return null; // sun never reaches this altitude (polar)
  return rad2deg(Math.acos(cosT)) / 15;
};

// Asr hour angle. Shafi'i/Maliki/Hanbali: tan(alt) = 1/(1 + tan(|lat-decl|))
const asrHourAngle = (lat, decl, step = 1) => {
  const t = step + Math.tan(deg2rad(Math.abs(lat - decl)));
  const alt = -rad2deg(Math.atan(1 / t));
  return hourAngle(alt, lat, decl);
};

/**
 * Compute today's prayer times at a given location.
 * Returns `{ fajr, sunrise, dhuhr, asr, maghrib, isha }` as Date objects
 * in the user's local time zone.
 */
export const getPrayerTimes = ({ lat, lon, date = new Date() } = {}) => {
  if (lat == null || lon == null) return null;
  // Noon-anchored day with the user's local midnight.
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const jd = julianDay(local);
  const { decl, EqT } = sunPosition(jd);
  const tzOffsetH = -local.getTimezoneOffset() / 60;
  const dhuhrH = 12 + tzOffsetH - lon / 15 - EqT;
  const buildDate = (hour) => {
    if (hour == null || !isFinite(hour)) return null;
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    const d = new Date(local);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const fajrAlt = 18;   // MWL Fajr angle
  const ishaAlt = 17;   // MWL Isha angle
  const sunAlt  = 0.833; // standard apparent sunrise/sunset (includes refraction)
  const haSun  = hourAngle(sunAlt,  lat, decl);
  const haFajr = hourAngle(fajrAlt, lat, decl);
  const haIsha = hourAngle(ishaAlt, lat, decl);
  const haAsr  = asrHourAngle(lat, decl, 1); // Shafi'i default
  return {
    fajr:    buildDate(dhuhrH - haFajr),
    sunrise: buildDate(dhuhrH - haSun),
    dhuhr:   buildDate(dhuhrH),
    asr:     buildDate(dhuhrH + haAsr),
    maghrib: buildDate(dhuhrH + haSun),
    isha:    buildDate(dhuhrH + haIsha),
  };
};

/**
 * Given today's prayer-time object, return which prayer is currently
 * active + which comes next. Useful for a small compass widget UI.
 */
export const getActivePrayer = (times, now = new Date()) => {
  if (!times) return null;
  const order = [
    { key: 'fajr',    label: 'Fajr' },
    { key: 'sunrise', label: 'Sunrise' },
    { key: 'dhuhr',   label: 'Dhuhr' },
    { key: 'asr',     label: 'Asr' },
    { key: 'maghrib', label: 'Maghrib' },
    { key: 'isha',    label: 'Isha' },
  ];
  for (let i = 0; i < order.length; i += 1) {
    const t = times[order[i].key];
    if (t && now < t) {
      return {
        current: i === 0 ? { key: 'isha', label: 'Isha (yesterday)' } : order[i - 1],
        next:    order[i],
        nextAt:  t,
      };
    }
  }
  // Past Isha — next is tomorrow's Fajr.
  return { current: order[5], next: { key: 'fajrNext', label: 'Fajr (tomorrow)' }, nextAt: null };
};

export const formatHM = (date) => {
  if (!date) return '—';
  const h = date.getHours();
  const m = date.getMinutes();
  const am = h < 12;
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
};

export default { getPrayerTimes, qiblaBearing, getActivePrayer, formatHM, KAABA };
